import { Field } from 'formik';
import { useState } from 'react';
import { CountryData } from 'react-phone-input-2';
import formikMetaToState from '../../helpers/formikMetaToState';
import { PhoneInput, PhoneInputProps } from '../PhoneInput';

const UK_DIAL_CODE = '44';

/**
 * The number after the dial code as it is kept: a UK number typed the national
 * way (07911…) keeps its trunk 0 after +44, which the UK mask has no room for —
 * it used to push the last digit out (PPL-4147) — so the 0 is dropped.
 */
const keptNational = (dialCode: string, national: string) =>
  dialCode === UK_DIAL_CODE ? national.replace(/^0+/, '') : national;

/**
 * The dial code is locked (`countryCodeEditable={false}`), and the library
 * refuses any edit that does not leave it in place: deleting, typing or pasting
 * over a selection that covers it — or with the caret inside it — did nothing
 * at all. Such an edit is applied to the number after the dial code instead,
 * the dial code itself staying as it is. Returns the dial code and the
 * resulting national digits, or `undefined` when the edit starts after the
 * dial code (the library handles that edit itself).
 */
const editOverDialCode = (input: HTMLInputElement, inserted: string) => {
  const prefix = input.value.match(/^\+\d+/)?.[0];
  const { selectionStart: start, selectionEnd } = input;
  if (prefix === undefined || start === null || selectionEnd === null || start >= prefix.length) {
    return undefined;
  }
  // a selection ending inside the dial code replaces nothing after it
  const end = Math.max(selectionEnd, prefix.length);
  const dialCode = prefix.slice(1);
  let insertedDigits = inserted.replace(/\D/g, '');
  // a pasted international number brings the dial code along
  if (/^\s*(\+|00)/.test(inserted)) {
    insertedDigits = insertedDigits.replace(/^00/, '');
    if (insertedDigits.startsWith(dialCode)) {
      insertedDigits = insertedDigits.slice(dialCode.length);
    }
  }
  return { dialCode, national: insertedDigits + input.value.slice(end).replace(/\D/g, '') };
};

export const FormikPhoneInput = (
  props: PhoneInputProps & {
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
  },
) => {
  const { name } = props;
  // store last value from input, to reuse dialCode, otherwise ReactPhoneInput breaks
  const [lastValue, setLastValue] = useState('');

  return (
    <Field name={name}>
      {({ field, meta, form }: any) => {
        const { error, state } = formikMetaToState(meta);

        const setNumber = (shown: string, national: string) => {
          setLastValue(shown);
          form.setFieldValue(field.name, national === '' ? '' : shown, true /*validate*/);
        };

        const applyEditOverDialCode = (event: React.SyntheticEvent<HTMLInputElement>, inserted: string) => {
          const edit = editOverDialCode(event.currentTarget, inserted);
          if (edit === undefined) {
            return;
          }
          event.preventDefault();
          const national = keptNational(edit.dialCode, edit.national);
          setNumber(`+${edit.dialCode}${national}`, national);
        };

        return (
          <PhoneInput
            {...field}
            {...props}
            error={error}
            state={state}
            value={!field.value ? lastValue : field.value}
            onChange={(value, data, _, formattedValue) => {
              const { dialCode } = data as CountryData;
              const typed = value.slice(dialCode.length);
              const national = keptNational(dialCode, typed);
              setNumber(national === typed ? formattedValue : `+${dialCode}${national}`, national);
            }}
            onKeyDown={(event) => {
              props.onKeyDown?.(event);
              // a caller's handler that cancels the key cancels the edit too
              if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) {
                return;
              }
              if (event.key === 'Backspace' || event.key === 'Delete') {
                applyEditOverDialCode(event, '');
              } else if (/^\d$/.test(event.key)) {
                applyEditOverDialCode(event, event.key);
              }
            }}
            inputProps={{
              ...props.inputProps,
              onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
                // the library types inputProps as a plain object
                (props.inputProps as React.InputHTMLAttributes<HTMLInputElement> | undefined)?.onPaste?.(event);
                if (!event.defaultPrevented) {
                  applyEditOverDialCode(event, event.clipboardData.getData('text'));
                }
              },
            }}
            onBlur={() => {
              form.setFieldTouched(
                field.name,
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
        );
      }}
    </Field>
  );
};
